import React from 'react';
import { Link as LinkIcon, Loader2 } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { Language } from '../translations';

interface UrlFormProps {
  url: string;
  loading: boolean;
  darkMode: boolean;
  language: Language;
  onUrlChange: (url: string) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
}

export function UrlForm({ url, loading, darkMode, language, onUrlChange, onSubmit }: UrlFormProps) {
  const { t } = useTranslation(language);

  const handlePaste = async (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    try {
      new URL(pastedText);
      onUrlChange(pastedText);
    } catch {
      // Invalid URL, just use the pasted text as is
      onUrlChange(pastedText);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div>
        <label htmlFor="url" className="block text-lg font-medium mb-2 text-orange-500">
          {t('urlForm', 'label')}
        </label>
        <div className="relative">
          <LinkIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-orange-500 w-5 h-5" />
          <input
            type="url"
            id="url"
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            onPaste={handlePaste}
            placeholder={t('urlForm', 'placeholder')}
            className={`w-full pl-12 pr-4 py-3 rounded-lg border-2 ${
              darkMode 
                ? 'bg-black border-orange-500 text-white placeholder-orange-300/50' 
                : 'bg-white border-orange-500 text-black placeholder-orange-300'
            } focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors`}
            required
            pattern="https?://.*"
            title="Voer een geldige URL in die begint met http:// of https://"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-orange-500 text-white py-4 px-6 rounded-lg font-medium 
                 hover:bg-orange-600 transition-all disabled:bg-orange-400 
                 disabled:cursor-not-allowed flex items-center justify-center
                 transform hover:scale-[1.02] active:scale-[0.98]
                 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin mr-3 w-6 h-6" />
            <span>{t('urlForm', 'generating')}</span>
          </>
        ) : (
          <span className="text-lg">{t('urlForm', 'button')}</span>
        )}
      </button>
    </form>
  );
}